// Times are stored in UTC and shown in South African time (RK-SYS-002 conventions).
export const SA_TIME_ZONE = "Africa/Johannesburg";

export function formatSADate(value: string | Date | null | undefined): string {
  if (!value) return "—";
  const d = typeof value === "string" ? (/^\d{4}-\d{2}-\d{2}$/.test(value) ? new Date(`${value}T12:00:00Z`) : new Date(value)) : value;
  return new Intl.DateTimeFormat("en-ZA", { timeZone: SA_TIME_ZONE, day: "numeric", month: "short", year: "numeric" }).format(d);
}

export function formatSADateTime(value: string | Date | null | undefined): string {
  if (!value) return "—";
  const d = typeof value === "string" ? new Date(value) : value;
  return new Intl.DateTimeFormat("en-ZA", { timeZone: SA_TIME_ZONE, day: "numeric", month: "short", hour: "2-digit", minute: "2-digit", hour12: false }).format(d);
}

/**
 * ED-RK-004: the age of figures kept on the phone, in words: "as at 14:05" for today, "as at 14:05 on 31 Oct 2026" for an
 * earlier day (South African time). Saved figures are never shown as if they were read just now.
 */
export function savedAsAt(value: string | Date, now: Date = new Date()): string {
  const d = typeof value === "string" ? new Date(value) : value;
  const time = new Intl.DateTimeFormat("en-ZA", { timeZone: SA_TIME_ZONE, hour: "2-digit", minute: "2-digit", hourCycle: "h23" }).format(d).replace(/[h.]/g, ":");
  return saToday(d) === saToday(now) ? `as at ${time}` : `as at ${time} on ${formatSADate(d)}`;
}

/** Today's date in South Africa as YYYY-MM-DD. */
export function saToday(now: Date = new Date()): string {
  const parts = new Intl.DateTimeFormat("en-CA", { timeZone: SA_TIME_ZONE, year: "numeric", month: "2-digit", day: "2-digit" }).format(now);
  return parts;
}

/** Whole days from a date (YYYY-MM-DD) to today in South Africa; half-open: the day itself counts as 0. */
export function daysSince(dateIso: string, now: Date = new Date()): number {
  const a = Date.parse(`${dateIso}T00:00:00Z`);
  const b = Date.parse(`${saToday(now)}T00:00:00Z`);
  return Math.floor((b - a) / 86_400_000);
}

export function relativeAge(value: string | Date, now: Date = new Date()): string {
  const d = typeof value === "string" ? new Date(value) : value;
  const mins = Math.round((now.getTime() - d.getTime()) / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins} min ago`;
  const hrs = Math.round(mins / 60);
  if (hrs < 24) return `${hrs} h ago`;
  const days = Math.round(hrs / 24);
  return days === 1 ? "yesterday" : `${days} days ago`;
}

/**
 * One rule for every date range (owner's decision 16, BD-139): a range includes its end date, and a day runs from
 * midnight to midnight South African time. Dates are YYYY-MM-DD; a query asks for "on or after from" and "before the
 * day after to". South Africa keeps UTC+2 all year (no daylight saving), so a South African day starts at 22:00 UTC.
 */
export function dayAfter(iso: string): string {
  const d = new Date(`${iso.slice(0, 10)}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + 1);
  return d.toISOString().slice(0, 10);
}

/** The half-open bounds for a range that includes its end date: `before` is the day after `to`. */
export function dateRange(from?: string | null, to?: string | null): { from?: string; before?: string } {
  return { ...(from ? { from: from.slice(0, 10) } : {}), ...(to ? { before: dayAfter(to) } : {}) };
}

/** The instant a South African day starts, for timestamp columns: 2026-10-23 -> 2026-10-23T00:00:00+02:00. */
export function saDayStart(iso: string): string {
  return `${iso.slice(0, 10)}T00:00:00+02:00`;
}

/** The South African date of an instant (a timestamp near midnight belongs to the South African day it falls in). */
export function saDateOf(value: string | Date): string {
  return saToday(typeof value === "string" ? new Date(value) : value);
}

/** Applies a range that includes its end date to a query on a date column (BD-139); either end may be left open. */
export function applyDateRange<Q extends { gte(column: string, value: string): Q; lt(column: string, value: string): Q }>(q: Q, column: string, from?: string | null, to?: string | null): Q {
  const r = dateRange(from, to);
  let out = q;
  if (r.from) out = out.gte(column, r.from);
  if (r.before) out = out.lt(column, r.before);
  return out;
}
